import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import AppContainer from 'components/AppContainer';
import { createStructuredSelector } from 'reselect';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectProfileFilters } from 'containers/Auth/selectors';
import {
  makeSelectClientDetails,
  makeSelectRegion,
} from 'containers/Main/selectors';
import _orderBy from 'lodash/orderBy';
import _uniqBy from 'lodash/uniqBy';
import {
  makeSelectClientLanding,
  makeSelectPageLanding,
} from 'containers/ClientLanding/selectors';
import ClientResources from 'components/ClientResources';
import { useQuery } from 'react-query';
import getRegionBySlug from 'containers/Main/services/getRegionBySlug.gql';
import ListTitle from 'components/ListTitle';
import ClassNames from 'classnames';
import useContentfulLocale from 'components/useContentfulLocale';
import GetLinkResources from 'components/useClientResources/services/getLinkResources';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#fff',
    marginTop: 54,
  },
  mt50: {
    marginTop: 50,
  },
  landingRoot: {
    backgroundColor: '#fff',
    marginTop: 13,
    marginBottom: 0,
  },
  cardRoot: {
    height: 'auto',
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
  },
  landingCardRoot: {
    padding: '40px 0 0',
  },
  links: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    '& p': {
      ...theme.typography.subtitle1,
      fontFamily: 'MadaRegular',
      margin: '0 13px 0 0',
      fontWeight: 400,
    },
    '& a': {
      ...theme.typography.subtitle1,
      fontFamily: 'MadaRegular',
      fontWeight: 400,
      marginLeft: 5,
    },
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
    },
  },
  header: {
    marginBottom: 27,
  },
  titleWrapper: {
    '& h2': {
      overflow: 'hidden',
      [theme.breakpoints.up('600')]: {
        '&::after': {
          content: `""`,
          height: '0.6em',
          verticalAlign: 'bottom',
          width: '100%',
          marginRight: '-100%',
          marginLeft: 20,
          borderTop: '1px solid #E2E2E2',
          display: 'inline-block',
        },
      },
    },
  },
}));

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientLanding: makeSelectClientLanding(),
  pageLanding: makeSelectPageLanding(),
  profileFilters: makeSelectProfileFilters(),
  region: makeSelectRegion(),
});

function ClientFactoids({ mt50, isLanding }) {
  const {
    clientDetails,
    clientLanding,
    pageLanding,
    profileFilters,
    region,
  } = useSelector(stateSelector);
  const classes = useStyles();
  const ctfLocaleFilter = useContentfulLocale();
  const [userResourcesSiteCopy] = useSiteCopySelector(['user-resources']);
  const name = _get(clientDetails, 'name', '');
  const finalLanding = isLanding ? pageLanding : clientLanding;
  const {
    title = '',
    userTerm,
    slug,
    clientResourcesCollection,
  } = finalLanding;
  const isCovidCenter = slug === 'covid-19';
  const { data } = useQuery(
    !isCovidCenter && region && ['region', region],
    getRegionBySlug,
  );
  const { insuranceOptions, insuranceTags, state } = profileFilters;
  const clientId = _get(clientDetails, 'sys.id');
  const { data: linkResources = [] } = useQuery(
    clientId && [
      'home-page-links',
      {
        clientId,
        ctfLocaleFilter,
        insuranceTags,
        insuranceOptions,
        show: 'Home',
      },
    ],
    GetLinkResources,
  );

  if (_isEmpty(clientDetails)) return null;
  const clientResources = isCovidCenter
    ? _get(clientResourcesCollection, 'items', [])
    : _get(clientDetails, 'clientResourcesCollection.items', []);
  const finalTitle = _get(userResourcesSiteCopy, 'pageCopy.clientFactoidsLabel')
    .replace('<clientTitle>', isCovidCenter ? title : name)
    .replace('<userTerm>', userTerm || '');
  const hasMultipleLanding =
    _get(clientDetails, 'landingLinksCollection.items', []).length > 1;
  const landingClientResources = hasMultipleLanding
    ? _get(finalLanding, 'clientResourcesCollection.items', [])
    : [];
  const regionClientResources = _get(
    data,
    'clientResourcesCollection.items',
    [],
  );
  if (
    _isEmpty(clientResources) &&
    _isEmpty(regionClientResources) &&
    _isEmpty(linkResources)
  )
    return null;
  const finalClientResources = _orderBy(
    _uniqBy(
      [
        ...linkResources,
        ...clientResources,
        ...regionClientResources,
        ...landingClientResources,
      ],
      'sys.id',
    )
      .filter(el => !!el)
      .filter(resource => {
        const hasStateSet =
          Boolean(state) &&
          (!_isEmpty(_get(resource, 'fields.state')) ||
            !_isEmpty(_get(resource, 'state')));

        if (!hasStateSet) return true;

        const resourceState =
          _get(resource, 'fields.state') || _get(resource, 'state');
        return resourceState.includes(state);
      }),
    resource => {
      const order = _get(resource, 'homeOrder');
      return !order ? Number.NEGATIVE_INFINITY : order;
    },
    ['desc'],
  );

  return (
    <Grid
      item
      xs={12}
      className={ClassNames(classes.root, { [classes.mt50]: mt50 })}
    >
      <AppContainer customPadding>
        <Grid container spacing={2} className={classes.cardRoot}>
          <Grid item xs={12} className={classes.header}>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item xs={12} className={classes.titleWrapper}>
                <ListTitle title={finalTitle} />
              </Grid>
            </Grid>
          </Grid>
          <ClientResources data={finalClientResources} />
        </Grid>
      </AppContainer>
    </Grid>
  );
}

ClientFactoids.propTypes = {
  mt50: PropTypes.bool,
  isLanding: PropTypes.bool,
};

export default ClientFactoids;
